import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react'

const API_URL = import.meta.env.VITE_API_URL || ''

export interface User {
  id: string
  email: string
  name: string
  avatarUrl?: string
  smartAccountId: string
}

export interface Wallet {
  id: string
  chain: 'SOLANA' | 'ETHEREUM' | 'BASE' | 'HYPEREVM'
  address: string
  isDefault: boolean
}

interface CachedToken {
  token: string
  expiresAt: number // Unix timestamp
}

interface AuthContextType {
  user: User | null
  wallets: Wallet[]
  isLoading: boolean
  isAuthenticated: boolean
  login: () => void
  logout: () => Promise<void>
  refetch: () => Promise<void>
  /** Get automation token (24hr expiry, cached) */
  getAutomationToken: () => Promise<string>
  /** Get service-specific token for external service calls (cached) */
  getServiceToken: (serviceUrl: string) => Promise<string>
}

const AuthContext = createContext<AuthContextType | null>(null)

// Token cache with 5-minute buffer before expiry
const TOKEN_BUFFER_MS = 5 * 60 * 1000

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [wallets, setWallets] = useState<Wallet[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Token caches (using refs to avoid re-renders)
  const automationTokenCache = useRef<CachedToken | null>(null)
  const serviceTokenCache = useRef<Map<string, CachedToken>>(new Map())

  const fetchUser = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' })
      if (response.ok) {
        const data = await response.json()
        setUser(data.user)
        setWallets(data.wallets || [])
      } else {
        setUser(null)
        setWallets([])
      }
    } catch {
      setUser(null)
      setWallets([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchUser()
  }, [])

  const login = () => {
    // Redirect to auth login endpoint
    window.location.href = `${API_URL}/api/auth/login`
  }

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' })
    } finally {
      setUser(null)
      setWallets([])
      // Clear token caches on logout
      automationTokenCache.current = null
      serviceTokenCache.current.clear()
    }
  }

  /**
   * Get automation token, using cache if valid
   */
  const getAutomationToken = async (): Promise<string> => {
    // Check cache
    const cached = automationTokenCache.current
    if (cached && cached.expiresAt > Date.now() + TOKEN_BUFFER_MS) {
      return cached.token
    }

    // Fetch new token
    const response = await fetch(`${API_URL}/api/auth/automation-token`, {
      method: 'POST',
      credentials: 'include',
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(error.error || 'Failed to get automation token')
    }

    const data = await response.json()

    // Cache the token
    automationTokenCache.current = {
      token: data.access_token,
      expiresAt: Date.now() + (data.expires_in * 1000),
    }

    return data.access_token
  }

  /**
   * Get service-specific token for external DAIN service calls
   */
  const getServiceToken = async (serviceUrl: string): Promise<string> => {
    // Check cache
    const cached = serviceTokenCache.current.get(serviceUrl)
    if (cached && cached.expiresAt > Date.now() + TOKEN_BUFFER_MS) {
      return cached.token
    }

    // Fetch new token via API
    const response = await fetch(`${API_URL}/api/auth/service-token`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ serviceUrl }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(error.error || 'Failed to get service token')
    }

    const data = await response.json()

    // Cache the token
    serviceTokenCache.current.set(serviceUrl, {
      token: data.serviceToken,
      expiresAt: Date.now() + (data.expiresIn * 1000),
    })

    return data.serviceToken
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        wallets,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        refetch: fetchUser,
        getAutomationToken,
        getServiceToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
